/**
 * Predictions API service
 */

import apiClient from './api';
import {
  LessonV1_SpineTagsPrediction,
  Product,
  ExperimentVersion,
  PaginatedResponse,
  PredictionFilters,
  UniqueFilterValues,
} from '../types';

export const predictionsApi = {
  /**
   * Get list of prediction IDs that have S3 data
   */
  async getS3PredictionIds(
    product: Product,
    experiment: ExperimentVersion
  ): Promise<string[]> {
    const response = await apiClient.get<{ ids: string[] }>('/predictions/s3-ids', {
      params: { product, experiment },
    });
    return response.data.ids;
  },

  /**
   * Get paginated list of predictions
   */
  async list(
    product: Product,
    experiment: ExperimentVersion,
    options?: {
      filters?: PredictionFilters;
      page?: number;
      pageSize?: number;
      lastEvaluatedKey?: Record<string, any>;
    }
  ): Promise<PaginatedResponse<LessonV1_SpineTagsPrediction>> {
    const params: Record<string, any> = {
      product,
      experiment,
      ...options?.filters,
      page: options?.page,
      pageSize: options?.pageSize,
    };

    // Include lastEvaluatedKey if provided
    if (options?.lastEvaluatedKey) {
      params.lastEvaluatedKey = JSON.stringify(options.lastEvaluatedKey);
    }

    const response = await apiClient.get<PaginatedResponse<LessonV1_SpineTagsPrediction>>(
      '/predictions',
      { params }
    );
    return response.data;
  },

  /**
   * Get unique filter values for dropdowns
   */
  async getFilterValues(
    product: Product,
    experiment: ExperimentVersion
  ): Promise<UniqueFilterValues> {
    const response = await apiClient.get<UniqueFilterValues>('/predictions/filter-values', {
      params: { product, experiment },
    });
    return response.data;
  },

  /**
   * Get single prediction by ID with S3 data
   */
  async getById(
    product: Product,
    experiment: ExperimentVersion,
    id: string
  ): Promise<LessonV1_SpineTagsPrediction> {
    const response = await apiClient.get<LessonV1_SpineTagsPrediction>(`/predictions/${id}`, {
      params: { product, experiment },
    });
    return response.data;
  },

  /**
   * Update prediction
   */
  async update(
    product: Product,
    experiment: ExperimentVersion,
    id: string,
    updates: Partial<LessonV1_SpineTagsPrediction>
  ): Promise<LessonV1_SpineTagsPrediction> {
    const response = await apiClient.put<LessonV1_SpineTagsPrediction>(
      `/predictions/${id}`,
      updates,
      {
        params: { product, experiment },
      }
    );
    return response.data;
  },

  /**
   * Create new prediction
   */
  async create(data: Partial<LessonV1_SpineTagsPrediction>): Promise<LessonV1_SpineTagsPrediction> {
    const response = await apiClient.post<LessonV1_SpineTagsPrediction>('/predictions', data);
    return response.data;
  },

  /**
   * Update assignee for a prediction
   */
  async updateAssignee(
    product: Product,
    experiment: ExperimentVersion,
    id: string,
    assignee: string | null
  ): Promise<LessonV1_SpineTagsPrediction> {
    const response = await apiClient.patch<LessonV1_SpineTagsPrediction>(
      `/predictions/${id}/assignee`,
      { assignee },
      {
        params: { product, experiment },
      }
    );
    return response.data;
  },

  /**
   * Get raw lesson text from S3
   */
  async getRawLessonText(
    product: Product,
    experiment: ExperimentVersion,
    id: string
  ): Promise<string> {
    const response = await apiClient.get<{ text: string }>(
      `/predictions/${encodeURIComponent(id)}/raw-text`,
      {
        params: { product, experiment },
      }
    );
    return response.data.text;
  },

  /**
   * Start async tag prediction job
   * Returns immediately with a job ID for polling
   */
  async startAsyncPrediction(
    product: Product,
    experiment: ExperimentVersion,
    id: string
  ): Promise<AsyncPredictResponse> {
    const response = await apiClient.post<AsyncPredictResponse>(
      `/predictions/${encodeURIComponent(id)}/rerun-async`,
      {},
      {
        params: { product, experiment },
      }
    );
    return response.data;
  },

  /**
   * Poll job status for async prediction
   */
  async getJobStatus(
    id: string,
    jobId: string
  ): Promise<JobStatusResponse> {
    const response = await apiClient.get<JobStatusResponse>(
      `/predictions/${encodeURIComponent(id)}/job/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  /**
   * Get count of predictions for export confirmation
   */
  async getExportCount(
    product: Product,
    experiment: ExperimentVersion,
    filters?: PredictionFilters
  ): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/predictions/export/count', {
      params: { product, experiment, ...filters },
    });
    return response.data.count;
  },

  /**
   * Export predictions as CSV (returns blob URL)
   */
  async exportCSV(
    product: Product,
    experiment: ExperimentVersion,
    filters?: PredictionFilters
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await apiClient.get('/predictions/export', {
      params: { product, experiment, ...filters },
      responseType: 'blob',
    });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `content_export_${product}_${experiment}.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    return { blob: response.data, filename };
  },
};

// Async prediction types
export interface AsyncPredictResponse {
  job_id: string;
  status: 'INIT';
  message: string;
}

export interface JobStatusResponse {
  job_id: string;
  status: 'INIT' | 'PROCESSING' | 'SUCCEEDED' | 'FAILED';
  message: string;
  details: {
    product?: string;
    experiment?: string;
    lesson_id?: string;
    ranked_tags_count?: number;
    target_standard_ids_count?: number;
    tag_candidates_count?: number;
    lesson_text_length?: number;
  };
  started_at: string;
  completed_at: string | null;
  s3_key: string | null;
}
